import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import Icon, { IconSize } from "@/components/Icon/Icon";
import styles from "./BoxReferenceInput.module.css";
import { BoxReferenceInputViewModel } from "./BoxReferenceInputViewModel";

export interface BoxReferenceInputOption {
  id: string;
  label: string;
}

export interface BoxReferenceInputProps {
  value: string;
  options: BoxReferenceInputOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

export const BoxReferenceInput: React.FC<BoxReferenceInputProps> = view(
  BoxReferenceInputViewModel,
)<BoxReferenceInputProps>(({ viewModel }) => {
  return (
    <div className={styles.root} ref={viewModel.setRootRef}>
      <button
        type="button"
        className={`${styles.trigger} ${!viewModel.hasValue ? styles.triggerPlaceholder : ""}`}
        onClick={viewModel.toggle}
        disabled={viewModel.disabled}
        aria-haspopup="dialog"
        aria-expanded={viewModel.isOpen}
        aria-controls={viewModel.isOpen ? viewModel.listId : undefined}
      >
        <span className={styles.triggerText}>{viewModel.displayValue}</span>
        <Icon
          name="OutlinedArrowDown"
          size={IconSize.small}
          className={`${styles.triggerIcon} ${viewModel.isOpen ? styles.triggerIconOpen : ""}`}
        />
      </button>

      {viewModel.isOpen && (
        <div className={styles.layer} onMouseDown={viewModel.onLayerMouseDown}>
          <div
            className={styles.panel}
            role="dialog"
            aria-modal="false"
            aria-labelledby={viewModel.titleId}
            onMouseDown={viewModel.onPanelMouseDown}
          >
            <div className={styles.header}>
              <div className={styles.headerText}>
                <h3 id={viewModel.titleId} className={styles.title}>
                  Выберите коробку
                </h3>
                <span className={styles.count}>{viewModel.countLabel}</span>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={viewModel.close}
                aria-label="Закрыть выбор коробки"
                title="Закрыть выбор коробки"
              >
                <Icon name="Close" size={IconSize.small} />
              </button>
            </div>

            <label className={styles.searchWrap}>
              <Icon name="Search" size={IconSize.small} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                value={viewModel.searchValue}
                onChange={viewModel.onSearchChange}
                placeholder={viewModel.searchPlaceholder}
                autoFocus
                aria-controls={viewModel.listId}
              />
            </label>

            <div id={viewModel.listId} className={styles.list} role="listbox">
              {viewModel.allowEmpty && viewModel.hasValue && (
                <button
                  type="button"
                  className={styles.option}
                  onClick={viewModel.onEmptySelect}
                  role="option"
                  aria-selected="false"
                >
                  <span className={styles.optionText}>{viewModel.emptyLabel}</span>
                </button>
              )}

              {viewModel.hasFilteredOptions ? (
                viewModel.filteredOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`${styles.option} ${
                      viewModel.isOptionSelected(option) ? styles.optionSelected : ""
                    }`}
                    onClick={() => viewModel.selectOption(option)}
                    role="option"
                    aria-selected={viewModel.isOptionSelected(option)}
                  >
                    <span className={styles.optionText}>{option.label}</span>
                    <span className={styles.optionMeta}>{option.id}</span>
                  </button>
                ))
              ) : (
                <div className={styles.emptyState}>{viewModel.emptyStateText}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
